import { FC, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useBasePath from "src/utils/hooks/useBasePath";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import {
  useChangeStatusCalendar,
  useDeleteCalendar,
} from "src/Pages/Company/Absences/api/useCalendars";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import OffIcon from "src/theme/Icons/Off";
import OnIcon from "src/theme/Icons/On";
import { ICalendar } from "src/Pages/Company/Absences/Calendars/Calendars.models";

interface ICalendarsListItemProps {
  calendar: ICalendar;
  isFetching: boolean;
}

const CalendarsListItem: FC<ICalendarsListItemProps> = ({
  calendar,
  isFetching,
}) => {
  const history = useHistory();
  const basePath = useBasePath();
  const companyId = useCompanyId();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );

  const { mutate: toggleActivation } = useChangeStatusCalendar(companyId);

  const { mutate: deleteCalendar } = useDeleteCalendar(companyId);

  const confirmDeactivation = useCallback(() => {
    if (typeof setModal === "function") {
      setModal({
        open: true,
        title: (
          <>
            {__("Czy na pewno chcesz dezaktywować kalendarz")} {calendar.name}?
          </>
        ),
        description: __(
          "Kalendarz nie jest powiązany z aktywnymi pracownikami i zostanie przeniesiony do zakładki „Nieaktywne”. Po tej akcji nie będzie można przypisać go do pracowników."
        ),
        actions: [
          {
            label: __("Anuluj"),
            variant: "outlined",
            callbackFn: () => {
              resetModal();
            },
          },
          {
            label: __("Dezaktywuj"),
            color: "error",
            variant: "contained",
            callbackFn: () => {
              toggleActivation({ calendarId: calendar.id, active: false });
              resetModal();
            },
          },
        ],
      });
    }
  }, [calendar.id, calendar.name, resetModal, setModal, toggleActivation]);

  const confirmActivation = useCallback(() => {
    if (typeof setModal === "function") {
      setModal({
        open: true,
        title: (
          <>
            {__("Czy na pewno chcesz aktywować kalendarz")} {calendar.name}?
          </>
        ),
        description: __(
          "Aktywacja umożliwi ponowne przypisywanie kalendarza do kont pracowników."
        ),
        actions: [
          {
            label: __("Anuluj"),
            variant: "outlined",
            callbackFn: () => {
              resetModal();
            },
          },
          {
            label: __("Aktywuj"),
            variant: "contained",
            callbackFn: () => {
              toggleActivation({ calendarId: calendar.id, active: true });
              resetModal();
            },
          },
        ],
      });
    }
  }, [calendar.id, calendar.name, resetModal, setModal, toggleActivation]);

  const confirmDeletion = useCallback(() => {
    if (typeof setModal === "function") {
      setModal({
        open: true,
        title: (
          <>
            {__("Czy na pewno chcesz usunąć kalendarz")} {calendar.name}?
          </>
        ),
        description: __(
          "Kalendarz nie jest powiązany z aktywnymi użytkownikami oraz złożonymi wnioskami i zostanie trwale usunięty z listy kalendarzy."
        ),
        actions: [
          {
            label: __("Anuluj"),
            variant: "outlined",
            callbackFn: () => {
              resetModal();
            },
          },
          {
            label: __("Usuń"),
            variant: "contained",
            color: "error",
            callbackFn: () => {
              deleteCalendar(calendar.id);
              resetModal();
            },
          },
        ],
      });
    }
  }, [calendar.id, calendar.name, deleteCalendar, resetModal, setModal]);

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      sx={(theme) => ({
        "&:not(:last-child)": {
          paddingBottom: "1.5rem",
          marginBottom: "1.5rem",
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
      })}
      data-testid="absences-calendars-list__item"
    >
      <Typography
        variant="subtitle1"
        component="h5"
        margin="0"
        data-testid="absences-calendars-list__item-name"
      >
        {calendar.name}
      </Typography>
      <Stack direction="row" marginLeft="auto" spacing={2}>
        <ActionButton
          label={__("Edytuj")}
          bgColor="info.main"
          bgColorHover="info.dark"
          color="white"
          disabled={isFetching}
          onClick={() =>
            history.push(`${basePath}/absences/calendars/${calendar.id}/edit`)
          }
          data-testid="absences-calendars-list__item-edit-action"
        >
          <EditNoteOutlinedIcon />
        </ActionButton>
        {canWriteAbsencesConfiguration && (
          <ActionButton
            label={__("Duplikuj")}
            bgColor="success.light"
            bgColorHover="success.dark"
            color="white"
            disabled={isFetching}
            onClick={() =>
              history.push(
                `${basePath}/absences/calendars/${calendar.id}/clone`
              )
            }
            data-testid="absences-calendars-list__item-clone-action"
          >
            <ContentCopyOutlinedIcon
              sx={{ transform: "rotate(90deg)" }}
              fontSize="small"
            />
          </ActionButton>
        )}
        {calendar.active ? (
          calendar.isDeactivatable && (
            <ActionButton
              label={__("Dezaktywuj")}
              bgColor="warning.light"
              bgColorHover="warning.main"
              color="white"
              disabled={isFetching}
              onClick={confirmDeactivation}
              data-testid="absences-calendars-list__item-deactivate-action"
            >
              <OffIcon />
            </ActionButton>
          )
        ) : (
          <ActionButton
            label={__("Aktywuj")}
            bgColor="success.light"
            bgColorHover="success.main"
            color="white"
            disabled={isFetching}
            onClick={confirmActivation}
            data-testid="absences-calendars-list__item-activate-action"
          >
            <OnIcon />
          </ActionButton>
        )}
        {calendar.isDeletable && (
          <ActionButton
            label={__("Usuń")}
            bgColor="error.main"
            bgColorHover="error.dark"
            color="white"
            disabled={isFetching}
            onClick={confirmDeletion}
            data-testid="absences-calendars-list__item-delete-action"
          >
            <DeleteOutline />
          </ActionButton>
        )}
      </Stack>
      <Typography variant="body2" flexBasis="100%" color="textSecondary">
        {calendar.description}
      </Typography>
    </Stack>
  );
};

export default CalendarsListItem;
